import { type AssetManifest, type EncounterView, TILE_SIZE } from '@shefira/shared';
import type Phaser from 'phaser';

/**
 * The lost chests of the roads (ADR 0093), drawn where the server says they
 * fell: shut until this player opened it, then open and faded. Unlike the
 * zone's chests they come and go with the `encounters` message, and they do
 * not block the road.
 */

/** What an opened chest keeps of its colour: still there, nothing more for this player. */
const OPENED_ALPHA = 0.55;

interface Drawn {
  image: Phaser.GameObjects.Image;
  done: boolean;
}

export class EncounterChestLayer {
  private readonly drawn = new Map<string, Drawn>();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly manifest: AssetManifest,
  ) {}

  /** The whole list, as the server last sent it: chests gone are taken away. */
  show(encounters: readonly EncounterView[]): void {
    const chests = encounters.filter((encounter) => encounter.kind === 'lost-chest');
    const kept = new Set(chests.map((chest) => chest.id));
    for (const [id, drawn] of this.drawn) {
      if (kept.has(id)) continue;
      drawn.image.destroy();
      this.drawn.delete(id);
    }
    for (const chest of chests) {
      if (this.drawn.get(chest.id)?.done === chest.done) continue;
      this.draw(chest);
    }
  }

  destroy(): void {
    for (const drawn of this.drawn.values()) drawn.image.destroy();
    this.drawn.clear();
  }

  private draw(chest: EncounterView): void {
    this.drawn.get(chest.id)?.image.destroy();
    this.drawn.delete(chest.id);
    const sprite =
      this.manifest.sprites[chest.done ? 'prop.chest-open' : 'prop.chest'] ??
      this.manifest.sprites['prop.chest'];
    if (sprite === undefined) return;
    const frame = this.scene.textures.getFrame(sprite.atlas, sprite.frame);
    const middle = chest.x * TILE_SIZE + TILE_SIZE / 2;
    const base = (chest.y + 1) * TILE_SIZE;
    // Whole pixels (CLAUDE.md rule 6), sorted with the characters by its base.
    const image = this.scene.add
      .image(Math.round(middle - frame.width / 2), base - frame.height, sprite.atlas, sprite.frame)
      .setOrigin(0, 0)
      .setDepth(base)
      .setAlpha(chest.done ? OPENED_ALPHA : 1);
    this.drawn.set(chest.id, { image, done: chest.done });
  }
}

/** Whether a tile falls on a road chest's sprite: its tile, its sides, and the row its lid rises into. */
export function onRoadChest(
  chest: { x: number; y: number },
  tile: { x: number; y: number },
): boolean {
  return Math.abs(tile.x - chest.x) <= 1 && tile.y >= chest.y - 1 && tile.y <= chest.y;
}
